/**
 * DD Chat Client Types
 *
 * Type definitions for the DD Chat UI components.
 * These extend the base types from edge-functions with UI-specific concerns.
 */

import type { DDChatMessage, DDStartupContext } from '@/lib/supabase/edge-functions';
import type { DDAssistantMode, DDSource } from './assistant.types';

// Re-export base types
export type { DDChatMessage, DDChatRole, DDStartupContext } from '@/lib/supabase/edge-functions';

// =============================================================================
// Extended Message Types (for RAG support)
// =============================================================================

/** Extended message metadata with source attribution */
export interface DDMessageMetadataExtended {
  tokens?: number;
  processingTime?: number;
  /** Legacy string sources (backward compat) */
  sources?: string[];
  /** Rich source objects (RAG mode) */
  richSources?: DDSource[];
  /** Which assistant mode generated this message */
  assistantMode?: DDAssistantMode;
}

/** Message with extended metadata for RAG support */
export interface DDChatMessageExtended extends Omit<DDChatMessage, 'metadata'> {
  metadata?: DDMessageMetadataExtended;
}

// =============================================================================
// UI State Types
// =============================================================================

export interface DDChatUIState {
  /** Whether the chat panel is visible */
  isOpen: boolean;
  /** Whether the chat is in compact/expanded mode */
  isExpanded: boolean;
  /** Current input value */
  inputValue: string;
  /** Whether user is typing */
  isTyping: boolean;
}

// =============================================================================
// Component Props Types
// =============================================================================

export interface DDChatMessageProps {
  /** The message to display */
  message: DDChatMessage;
  /** Whether this is the last message */
  isLast?: boolean;
  /** Whether the message is being generated */
  isStreaming?: boolean;
  /** Callback when user taps a suggested follow-up */
  onSuggestionTap?: (suggestion: string) => void;
}

/** Attached file for analysis */
export interface DDChatAttachment {
  uri: string;
  name: string;
  size?: number;
  mimeType?: string;
}

export interface DDChatInputProps {
  /** Current input value */
  value: string;
  /** Callback when value changes */
  onChangeText: (text: string) => void;
  /** Callback when user submits */
  onSubmit: () => void;
  /** Whether submission is disabled */
  disabled?: boolean;
  /** Placeholder text */
  placeholder?: string;
  /** Whether to auto-focus */
  autoFocus?: boolean;
  /** Enable file attachment */
  allowAttachment?: boolean;
  /** Current attachment */
  attachment?: DDChatAttachment | null;
  /** Callback when file is attached */
  onAttach?: (file: DDChatAttachment) => void;
  /** Callback to remove attachment */
  onRemoveAttachment?: () => void;
}

export interface DDChatContainerProps {
  /** Startup context for the chat */
  startupContext: DDStartupContext;
  /** Startup ID (for tracking) */
  startupId?: string;
  /** Whether to show in compact mode initially */
  initialCompact?: boolean;
  /** Use flex layout instead of fixed height (for full-screen mode) */
  flexLayout?: boolean;
  /** Callback when chat is closed */
  onClose?: () => void;
  /** Investor preferences */
  investorPreferences?: {
    focusAreas?: string[];
    riskTolerance?: 'low' | 'medium' | 'high';
    investmentStage?: string[];
  };
  /** Enable document attachment for analysis */
  allowAttachment?: boolean;
}

// =============================================================================
// Analysis Types
// =============================================================================

export type DDAnalysisFocus =
  | 'team'
  | 'market'
  | 'product'
  | 'traction'
  | 'financials'
  | 'risks'
  | 'competition'
  | 'exit'
  | 'general';

export interface DDQuickAction {
  id: string;
  label: string;
  icon: string;
  prompt: string;
  focus: DDAnalysisFocus;
}

export const DD_QUICK_ACTIONS: DDQuickAction[] = [
  {
    id: 'risks',
    label: 'Key Risks',
    icon: 'alert-triangle',
    prompt: 'What are the main risks I should consider with this investment?',
    focus: 'risks',
  },
  {
    id: 'team',
    label: 'Team Analysis',
    icon: 'users',
    prompt: 'Analyze the founding team and their ability to execute.',
    focus: 'team',
  },
  {
    id: 'traction',
    label: 'Traction Review',
    icon: 'trending-up',
    prompt: 'How strong is the traction for this stage? What metrics stand out?',
    focus: 'traction',
  },
  {
    id: 'market',
    label: 'Market Analysis',
    icon: 'pie-chart',
    prompt: 'Analyze the market opportunity and competitive landscape.',
    focus: 'market',
  },
  {
    id: 'valuation',
    label: 'Deal Terms',
    icon: 'dollar-sign',
    prompt: 'Are the funding terms and implied valuation reasonable for this stage?',
    focus: 'financials',
  },
  {
    id: 'questions',
    label: 'DD Questions',
    icon: 'help-circle',
    prompt: 'What are the most important questions I should ask the founders?',
    focus: 'general',
  },
];
